<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { onClickOutside } from "@vueuse/core";
import { NuxtLink } from "#components";

interface MenuItem {
  label: string;
  url?: string;
  target?: string;
  uppercase?: boolean;
  separator?: boolean;
  bg?: boolean;
  items?: MenuItem[];
  variant?: string;
}

const props = defineProps<{
  items: MenuItem[];
  profileItems: MenuItem[];
}>();

const route = useRoute();
const auth = useAuthStore();

const menuState = reactive<Record<string, boolean>>( {
  Profile: false,
} );

function active ( item: MenuItem ) {
  return item.url === route.fullPath;
}

function closeAllMenus () {
  Object.keys( menuState ).forEach( ( key ) => {
    menuState[key] = false;
  } );
}

function menuOpen ( item: MenuItem ) {
  return !!menuState[item.label];
}

function toggleMenu ( item: MenuItem ) {
  if ( !item.items || item.items.length === 0 ) {
    return;
  }

  Object.keys( menuState ).forEach( ( key ) => {
    if ( key !== item.label ) {
      menuState[key] = false;
    }
  } );

  menuState[item.label] = !menuState[item.label];
}

function generateId ( item: MenuItem, parentItem: MenuItem | null = null ): string {
  let result = "main-navigation__";

  if ( parentItem && parentItem.label ) {
    const parentLabel = parentItem.label;
    result += parentLabel.includes( " " )
      ? `${parentLabel.replace( / /g, "-" )}-`
      : `${parentLabel}-`;
  }

  if ( item.label ) {
    const itemLabel = item.label;
    result += itemLabel.includes( " " )
      ? itemLabel.replace( / /g, "-" )
      : itemLabel;
  }

  return result.toLowerCase();
}

// Watch for route changes
watch(
  () => route.fullPath,
  () => {
    closeAllMenus();
  },
);

const profileItem = computed( () => ( { label: "Profile", items: props.profileItems } ) );

// Handle click outside
const navRef = ref<HTMLElement | null>( null );

onClickOutside( navRef, () => {
  closeAllMenus();
} );
</script>

<template>
  <nav
    ref="navRef"
    class="main-navigation">
    <ul class="main-navigation__list">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="main-navigation__item"
        :class="{
          'main-navigation__item--open': menuOpen(item),
          'main-navigation__item--separator': item.separator,
          'main-navigation__item--active': active(item),
          'main-navigation__item--bg': item.bg,
        }">
        <NuxtLink
          v-if="item.url"
          :id="generateId(item)"
          class="main-navigation__link"
          :class="{
            'main-navigation__link--uppercase': item.uppercase,
          }"
          :to="item.url"
          :target="item.target || '_self'"
          :external="isMediaAssetUrl(item.url)"
          @click="toggleMenu(item)">
          {{ item.label }}
        </NuxtLink>
        <button
          v-else
          :id="generateId(item)"
          class="main-navigation__link"
          :class="{
            'main-navigation__link--uppercase': item.uppercase,
          }"
          @click="toggleMenu(item)">
          {{ item.label }}
        </button>

        <ul
          class="navigation-dropdown"
          :class="{ 'navigation-dropdown--open': menuOpen(item) }">
          <li
            v-for="(subItem, subIndex) in item.items"
            :key="subIndex"
            class="navigation-dropdown__item">
            <NuxtLink
              v-if="subItem.url"
              :id="generateId(subItem, item)"
              class="navigation-dropdown__link"
              :to="subItem.url"
              :target="subItem.target || '_self'"
              :external="isMediaAssetUrl(subItem.url)"
              @click="toggleMenu(subItem)">
              {{ subItem.label }}
            </NuxtLink>
            <button
              v-else
              :id="generateId(subItem, item)"
              class="navigation-dropdown__link"
              @click="toggleMenu(subItem)">
              {{ subItem.label }}
            </button>
          </li>
        </ul>
      </li>

      <li
        v-if="!auth.isLoggedIn"
        class="main-navigation__item">
        <nuxt-link
          :id="`main-navigation__button-login`"
          class="main-navigation__link main-navigation__link--uppercase"
          to="/login">
          Log in
        </nuxt-link>
      </li>
    </ul>

    <template v-if="auth.isLoggedIn">
      <div class="profile-wrapper">
        <display-picture
          :id="`main-navigation__profile-image`"
          :picture="auth ? auth.profileImage : null"
          :name="auth ? auth.webAccount?.firstName : null"
          :loading="!auth"
          size="sm"
          round
          @click="toggleMenu(profileItem)" />

        <ul
          class="navigation-dropdown"
          :class="{ 'navigation-dropdown--open': menuOpen(profileItem) }">
          <li
            v-for="(item, index) in profileItems"
            :key="index"
            class="navigation-dropdown__item">
            <span class="navigation-dropdown__button-wrapper">
              <a-button
                :id="generateId(item)"
                size="md"
                look="pill"
                :variant="item.variant || 'primary'"
                class="main-navigation__button"
                :to="item.url">
                {{ item.label }}
              </a-button>
            </span>
          </li>
        </ul>
      </div>
    </template>
  </nav>
</template>

<style lang="scss" scoped>
@use "sass:color";

@import '~/assets/styles/_variables.scss';

.main-navigation {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  position: relative;

  &__list {
    padding: 0;
    list-style: none;
    display: flex;
    margin: 0;
    flex-wrap: wrap;
    align-items: stretch;
  }

  &__item {
    display: flex;
    align-items: center;
    position: relative;

    &:hover {
      color: var(--primary);
    }

    &--open,
    &--active {
      &::after {
        display: block;
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        border-bottom-color: var(--primary);
        border-bottom-style: solid;
        border-bottom-width: 0px;
      }
    }

    &--separator {
      // border-left: 1px solid #f2f2f2;
    }

    &--bg {
      // background: #f9f9f9;
    }
  }

  &__link {
    display: inline-block;
    padding: 10px 0px;
    margin: 0 10px;
    color: #fff;
    white-space: nowrap;
    font-weight: 400;
    text-decoration: none;
    cursor: pointer;
    appearance: none;
    border: 0;
    background: none;

    &:hover {
      //color: var(--primary);
      border-bottom: 1px solid #E6007E;
    }

    &--uppercase {
      text-transform: uppercase;
    }
  }

  &__button {
    margin-left: 30px;
    white-space: nowrap;
    align-self: center;
  }
}

.display-picture {
  cursor: pointer;
}

.profile-wrapper {
  margin-left: 15px;
  margin-right: 6px;
  display: flex;
  align-items: center;
}

.navigation-dropdown {
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 5px 0;
  visibility: hidden;
  position: absolute;
  top: calc(100% + -15px);
  right: 0;
  background: var(--bg);
  backdrop-filter: blur(5px);
  min-height: 30px;
  width: 200px;
  border-radius: 20px;
  box-shadow: 0 5px 5px rgba(0, 0, 0, 0.3);

  &__link {
    padding: 7px 10px;
    display: block;
    border-bottom: 1px solid #eee;
    text-decoration: none;

    &:hover {
      background: color.adjust($accent-1, $alpha: -90%);
      border-bottom: 1px solid var(--primary);
    }
  }

  &__button-wrapper {
    display: block;
    width: 100%;
    padding: 5px 10px;
  }

  &__item:last-child {
    .navigation-dropdown__button-wrapper {
      padding-bottom: 10px;
    }
  }

  .main-navigation__button {
    margin: 0;
    width: 100%;

    &:hover {
      color: #000 !important;
    }
  }

  &--open {
    visibility: visible;
  }
}

.dark-mode .main-navigation__item--separator {
  border-left-color: var(--dark-border-color) !important;
}

.dark-mode .navigation-dropdown__link {
  border-bottom-color: var(--dark-border-color) !important;
}

.dark-mode .main-navigation__item--bg {
  background: #202129;
}

.main-navigation__link--uppercase {
  border: 1px solid #201D25;
  padding-left: 20px;
  padding-right: 20px;
  border-radius: 40px;
}
.main-navigation__link--uppercase:hover {
  border: 1px solid #E6007E;
}

#main-navigation__join {
  margin-left: 40px;
}
</style>
